import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { friendlyError } from "@/lib/errors";
import { AuthLegalLinks } from "@/components/layout/AuthLegalLinks";

function appPath() {
  const base = __BASE_PATH__.split("/").filter(Boolean).join("/");
  return base ? `/${base}` : "";
}

export default function ForgotPassword() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const redirectTo = `${window.location.origin}${appPath()}/reset-password`;
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      // Don't reveal whether the email is registered. A "user not found" result is
      // treated the same as a successful send to avoid account enumeration.
      if (resetError && !/user not found|email not found|not found/i.test(resetError.message)) {
        setError(friendlyError(resetError.message));
        setLoading(false);
        return;
      }
      setSent(true);
    } catch {
      setError("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8">
        <div className="flex items-center justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center mr-3">
            <i className="ri-stack-line text-white" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Tradivo</h1>
        </div>

        {sent ? (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-send-line text-emerald-600 text-xl" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Check your email</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              If an account exists for <span className="font-medium text-slate-700">{email.trim()}</span>, we've sent a link to reset your password. It may take a minute to arrive.
            </p>
            <p className="text-sm text-slate-400 mt-4">Didn't get it? Check your spam folder, or try again below.</p>
            <button
              onClick={() => { setSent(false); setEmail(""); }}
              className="mt-4 text-sm font-medium text-emerald-600 hover:text-emerald-700"
            >
              Send again
            </button>
          </div>
        ) : user ? (
          <div className="text-center">
            <p className="text-sm text-slate-600 mb-4">You're already signed in.</p>
            <Link to="/dashboard" className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
              Go to dashboard
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-slate-800 text-center mb-1">Reset your password</h2>
            <p className="text-sm text-slate-500 text-center mb-6">Enter the email you use to sign in</p>

            {error && (
              <div role="alert" className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  id="forgot-email"
                  type="email"
                  name="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  placeholder="you@company.com"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-medium rounded-lg text-sm transition-colors whitespace-nowrap"
              >
                {loading ? "Sending..." : "Send recovery link"}
              </button>
            </form>
          </>
        )}

        <p className="text-sm text-slate-500 text-center mt-4">
          Remembered your password?{" "}
          <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
            Sign in
          </Link>
        </p>
        <AuthLegalLinks />
      </div>
    </div>
  );
}